/* Collection size quick view. Cart submissions use the theme's delegated handler. */
(() => {
  let active;
  const assetBase = new URL(document.currentScript.src, location.href);
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  const money = cents => window.themeVendor?.Currency?.formatMoney?.(cents, window.theme?.moneyFormat) || (cents / 100).toFixed(2);

  function openSizeDrawer(drawer, trigger) {
    if (!drawer) return;
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.offsetHeight;
    drawer.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    const closeBtn = drawer.querySelector('[data-quick-size-close]');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function closeSizeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    const trigger = drawer.closest('[data-cherry-quick-content]')?.querySelector('[data-quick-size-trigger]');
    trigger?.setAttribute('aria-expanded', 'false');
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    setTimeout(() => {
      if (!drawer.classList.contains('is-open')) {
        drawer.setAttribute('hidden', '');
      }
    }, 350);
  }

  async function open(trigger) {
    if (active) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'cherry-quick';
    dialog.setAttribute('aria-label', 'Product quick view');
    dialog.innerHTML = '<button type="button" class="cherry-quick__close" aria-label="Close product quick view" autofocus><img width="24" height="24" alt=""></button><div data-quick-host><p role="status">Loading product…</p></div>';
    dialog.querySelector('img').src = new URL('cherry-look-close.svg', assetBase).href;
    const controller = new AbortController();
    active = { dialog, controller };
    const host = dialog.querySelector('[data-quick-host]');
    dialog.addEventListener('close', () => {
      controller.abort();
      dialog.remove();
      document.documentElement.classList.remove('cherry-quick-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
      active = undefined;
    }, { once: true });
    dialog.querySelector('.cherry-quick__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    });
    dialog.addEventListener('cancel', event => {
      const openDrawer = dialog.querySelector('[data-quick-size-drawer].is-open');
      if (openDrawer) {
        event.preventDefault();
        closeSizeDrawer(openDrawer);
      }
    });
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        const openDrawer = dialog.querySelector('[data-quick-size-drawer].is-open');
        if (openDrawer) {
          event.preventDefault();
          event.stopPropagation();
          closeSizeDrawer(openDrawer);
          return;
        }
      }
      if (event.key !== 'Tab') return;
      const container = dialog.querySelector('[data-quick-size-drawer].is-open') || dialog;
      const controls = [...container.querySelectorAll('a[href],button,input,select,summary,[tabindex]')].filter(el => el.tabIndex >= 0 && !el.disabled && el.getClientRects().length && !el.closest('[hidden]'));
      if (!controls.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    document.body.append(dialog);
    document.documentElement.classList.add('cherry-quick-open');
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();
    const url = new URL(trigger.dataset.href, location.origin);
    url.searchParams.set('section_id', 'api-cherry-quickview');
    url.searchParams.set('variant', trigger.dataset.vid);
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    async function load() {
      host.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Product unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-cherry-quick-content]');
        if (!content) throw new Error('Product unavailable');
        if (controller.signal.aborted) return;
        host.replaceChildren(content);
        setup(dialog, content, trigger.dataset.vid, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) return;
        host.innerHTML = '<p role="alert">Unable to load this product. Please try again.</p><button type="button">Try again</button>';
        host.querySelector('button').addEventListener('click', load, { once: true });
      } finally { host.removeAttribute('aria-busy'); }
    }
    await load();
  }

  function setup(dialog, content, variantId, signal) {
    const product = JSON.parse(content.querySelector('[data-quick-product]').textContent);
    const prices = JSON.parse(content.querySelector('[data-quick-prices]').textContent);
    const groups = [...content.querySelectorAll('[data-quick-option]')];
    const form = content.querySelector('[data-quick-form]');
    const button = form.querySelector('.cherry-quick__submit');
    const notify = form.querySelector('[data-quick-notify]');
    const email = form.querySelector('[data-quick-email]');
    const status = form.querySelector('[data-quick-status]');
    let variant = product.variants.find(v => String(v.id) === String(variantId)) || product.variants[0];
    let subscribing = false;
    groups.forEach(group => {
      const value = variant.options[Number(group.dataset.quickOption)];
      const select = group.querySelector('select');
      if (select) select.value = value;
      else group.querySelectorAll('input').forEach(input => { input.checked = input.value === value; });
    });
    const values = () => groups.map(group => [Number(group.dataset.quickOption), group.querySelector('select')?.value || group.querySelector('input:checked')?.value]).sort((a,b) => a[0]-b[0]).map(entry => entry[1]);
    function update() {
      const selected = values();
      variant = product.variants.find(v => !groups.length || v.options.every((value,index) => value === selected[index]));
      const soldOut = variant && !variant.available;
      notify.hidden = !soldOut;
      email.disabled = !soldOut;
      email.required = Boolean(soldOut);
      button.disabled = !variant;
      button.toggleAttribute('data-add-to-cart', Boolean(variant?.available));
      button.querySelector('span').textContent = !variant ? 'Unavailable' : soldOut ? 'Notify me when available' : 'Add to bag';
      form.querySelector('[data-quick-variant]').value = variant?.id || '';
      if (variant) content.querySelector('[data-quick-price]').textContent = prices[variant.id] || money(variant.price);
      status.textContent = '';
      form.querySelector('[data-cart-errors-container]').replaceChildren();
      groups.forEach(group => {
        const index = Number(group.dataset.quickOption);
        const name = group.querySelector('[data-quick-color-name]');
        if (name) name.textContent = selected[index];
        const select = group.querySelector('select');
        if (!select) return;
        select.classList.toggle('is-sold-out', Boolean(soldOut));
        [...select.options].forEach(option => {
          const match = product.variants.find(v => v.options.every((value,i) => value === (i === index ? option.value : selected[i])));
          option.disabled = !match;
          option.dataset.available = String(Boolean(match?.available));
          option.dataset.label = option.value;
          option.textContent = option.value + (match && !match.available ? ' — Sold out' : '');
          if (option.selected) option.textContent = option.value;
        });
      });
    }
    form.addEventListener('change', event => {
      if (!event.target.closest('[data-quick-option]')) return;
      update();
    });
    update();
    form.addEventListener('click', event => {
      if (!event.target.closest('[data-add-to-cart]') || form.checkValidity()) return;
      event.preventDefault(); event.stopPropagation(); form.reportValidity();
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (!variant || subscribing) return;
      if (variant.available) { button.click(); return; }
      if (!form.reportValidity()) return;
      if (typeof window.BIS?.create !== 'function') {
        status.textContent = 'Notifications are temporarily unavailable. Please try again shortly.';
        return;
      }
      subscribing = true; button.disabled = true;
      groups.forEach(group => group.querySelectorAll('input,select').forEach(input => { input.disabled = true; }));
      status.textContent = 'Saving your notification…';
      try {
        // AMP Back in Stock's documented API accepts optional newsletter consent.
        const result = await new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('timeout')), 20000);
          try { window.BIS.create(email.value.trim(), variant.id, product.id, { accepts_marketing: form.querySelector('[data-quick-marketing]').checked }).then(data => { clearTimeout(timer); resolve(data); }); }
          catch (error) { clearTimeout(timer); reject(error); }
        });
        if (signal.aborted) return;
        if (result.status !== 'OK') throw new Error(Object.values(result.errors || {}).flat().join(' ') || 'Unable to save your notification. Please try again.');
        status.textContent = result.message || 'You’re signed up. We’ll email you when this size is back in stock.';
      } catch (error) {
        if (!signal.aborted) status.textContent = error.message === 'timeout' ? 'The request timed out. Please try again.' : error.message;
      } finally {
        subscribing = false; button.disabled = false;
        groups.forEach(group => group.querySelectorAll('input,select').forEach(input => { input.disabled = false; }));
      }
    });
    const gallery = content.querySelector('[data-quick-gallery]');
    let drag;
    let dragged = false;
    gallery.addEventListener('pointerdown', event => {
      if (!event.isPrimary || event.button !== 0) return;
      dragged = false;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, scroll: gallery.scrollLeft };
    });
    gallery.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const distance = event.clientX - drag.x;
      if (!dragged && event.pointerType !== 'mouse' && Math.abs(event.clientY - drag.y) > Math.max(5, Math.abs(distance))) {
        drag = undefined;
        return;
      }
      if (!dragged && Math.abs(distance) < 5) return;
      if (!dragged) {
        dragged = true;
        gallery.classList.add('is-dragging');
        gallery.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      gallery.scrollLeft = drag.scroll - distance;
    });
    const endDrag = event => {
      if (!drag || event.pointerId !== drag.id) return;
      drag = undefined;
      gallery.classList.remove('is-dragging');
      if (gallery.hasPointerCapture(event.pointerId)) gallery.releasePointerCapture(event.pointerId);
    };
    gallery.addEventListener('pointerup', endDrag);
    gallery.addEventListener('pointercancel', endDrag);
    gallery.addEventListener('lostpointercapture', event => { if (event.target === gallery) endDrag(event); });
    gallery.addEventListener('pointerleave', () => { if (!dragged) drag = undefined; });
    gallery.addEventListener('dragstart', event => event.preventDefault());
    gallery.addEventListener('click', event => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    }, true);
    const progress = content.querySelector('[data-quick-progress]');
    const syncGallery = () => {
      const max = gallery.scrollWidth - gallery.clientWidth;
      const scrollable = max > 1;
      progress.parentElement.hidden = !scrollable;
      const ratio = scrollable ? .275 : 1;
      progress.style.width = `${ratio * 100}%`;
      progress.style.marginLeft = `${scrollable ? gallery.scrollLeft / max * (1-ratio) * 100 : 0}%`;
    };
    gallery.addEventListener('scroll', syncGallery, { passive: true });
    const resize = new ResizeObserver(syncGallery); resize.observe(gallery);
    signal.addEventListener('abort', () => resize.disconnect(), { once: true });
    syncGallery();
    const recommendations = content.querySelector('[data-quick-recommendations]');
    function setupPairs() {
      const track = recommendations.querySelector('[data-quick-pairs]');
      if (!track) return;
      const prev = recommendations.querySelector('[data-quick-pair-prev]'), next = recommendations.querySelector('[data-quick-pair-next]');
      const controls = recommendations.querySelector('.cherry-product__pair-controls');
      const sync = () => {
        controls.hidden = track.scrollWidth <= track.clientWidth + 1;
        prev.disabled = track.scrollLeft < 1;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: motion() }));
      next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: motion() }));
      track.addEventListener('scroll', sync, { passive: true });
      const observer = new ResizeObserver(sync); observer.observe(track);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
      sync();
    }
    if (recommendations.querySelector('[data-quick-pairs]')) setupPairs();
    else fetch(recommendations.dataset.url, { signal }).then(response => response.ok ? response.text() : '').then(html => {
      if (signal.aborted) return;
      const pairs = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quick-recommendations]');
      if (pairs) { recommendations.innerHTML = pairs.innerHTML; setupPairs(); }
    }).catch(() => {});
    const sizeDrawer = content.querySelector('[data-quick-size-drawer]');
    const sizeTrigger = content.querySelector('[data-quick-size-trigger]');
    const sizeClose = content.querySelector('[data-quick-size-close]');
    if (sizeTrigger && sizeDrawer) {
      sizeTrigger.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        openSizeDrawer(sizeDrawer, sizeTrigger);
      });
    }
    if (sizeClose && sizeDrawer) {
      sizeClose.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        closeSizeDrawer(sizeDrawer);
      });
    }
    sizeDrawer?.querySelectorAll('[data-tabs-link]').forEach(tabLink => {
      tabLink.addEventListener('click', () => {
        const index = tabLink.dataset.tabsLink;
        sizeDrawer.querySelectorAll('[data-tabs-link]').forEach(l => l.classList.toggle('current', l === tabLink));
        sizeDrawer.querySelectorAll('[data-tab]').forEach(t => t.classList.toggle('current', t.dataset.tab === index));
      });
    });
    document.dispatchEvent(new CustomEvent('cherry:swatches:load', { detail: { root: content } }));
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-cherry-quick-view]');
    if (!trigger) return;
    event.preventDefault(); event.stopPropagation(); open(trigger);
  }, true);
  document.addEventListener('theme:cart:added', () => active?.dialog.close());
})();
